export const formatDateForDisplay = (dateStr: string): string => {
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

export const formatDateForAPI = (dateStr: string): string => {
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return dateStr;
};

export const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateLocal = (dateStr: string): string => {
  return formatDateForDisplay(dateStr);
};

export const getTodayLocal = (): string => {
  return getTodayDate();
};

export const dateToLocalString = (date: Date): string => {
  // Force local timezone to avoid UTC conversion issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const createLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const isDateInRangeLocal = (dateStr: string, startDate: string, endDate: string): boolean => {
  // Comparação simples de strings no formato YYYY-MM-DD
  return dateStr >= startDate && dateStr <= endDate;
};

export const getLast7DaysRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 6); // 6 days ago + today = 7 days
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getLast30DaysRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 29); // 29 days ago + today = 30 days
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getCurrentMonthRange = () => {
  const now = new Date();
  // Use local timezone to avoid UTC conversion issues
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const startDate = new Date(year, month, 1); // Primeiro dia do mês
  const endDate = new Date(year, month + 1, 0); // Último dia do mês
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getNext30DaysRange = () => {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() + 1); // Começa amanhã
  const endDate = new Date();
  endDate.setDate(startDate.getDate() + 29); // 30 days total (sem contar hoje)
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getYearToDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  
  const startDate = new Date(year, 0, 1); // Primeiro dia do ano
  const endDate = new Date(); // Hoje
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getMonthToDateRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const startDate = new Date(year, month, 1); // Primeiro dia do mês
  const endDate = new Date(); // Hoje
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getCurrentYearRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  
  const startDate = new Date(year, 0, 1); // Primeiro dia do ano
  const endDate = new Date(year, 11, 31); // Último dia do ano
  
  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const getAllPeriodRange = () => {
  return {
    startDate: '2020-01-01',
    endDate: '2030-12-31'
  };
};

export const getDateRangeForPeriod = (period: string) => {
  const now = new Date();
  let startDate: Date;
  let endDate: Date = new Date();

  switch (period) {
    case 'day':
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'week':
      startDate = new Date();
      startDate.setDate(now.getDate() - now.getDay());
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;
    case 'year':
      startDate = new Date(now.getFullYear(), 0, 1);
      endDate = new Date(now.getFullYear(), 11, 31);
      endDate.setHours(23, 59, 59, 999);
      break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
  }

  return {
    startDate: dateToLocalString(startDate),
    endDate: dateToLocalString(endDate)
  };
};

export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = formatDateForDisplay(startDate);
  const end = formatDateForDisplay(endDate);
  return `${start} - ${end}`;
};

export const isValidDate = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date.getTime());
};

export const parseDate = (dateStr: string): Date => {
  // Handle different date formats
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  return new Date(dateStr);
};

export const isToday = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

export const isThisMonth = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const today = new Date();
  return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};

export const isPastDue = (dateStr: string): boolean => {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

export const daysBetween = (date1: string, date2: string): number => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const formatDateHeader = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
};

export const getMonthStartEndDate = (selectedMonth: Date) => {
  const year = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();
  
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0);
  
  return {
    startDate,
    endDate
  };
};
